import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import React, { useEffect, useState } from "react";
import useEffectEvent from "@/lib/useEffectEvent";   // Step 7c · E3: the effect runs on its own deps only (see the file)
import CtNotSetNote from "@/components/CtNotSetNote";   // Batch 27 · D (M37)
import DateInput from "@/components/DateInput";
import { fmtDate } from "@/lib/dt";   // Batch 10 · B9: fmtDate for the holiday chips
import api, { toErrMsg } from "@/lib/api";
import { useEngagement } from "@/contexts/EngagementContext";
import { useI18n } from "@/contexts/I18nContext";
import StoredResultBanner from "@/components/StoredResultBanner";   // Batch 27 · Add-on 2 Step 2
import { useAuth } from "@/contexts/AuthContext";
import { useReviewRoles } from "@/lib/reviewRoles";   // STEP 3 · Rule 8: the server's role sets
import { LockedBanner } from "@/components/SignOffCard";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, AlertTriangle, ShieldCheck, ChevronsUpDown, Play, Download, RefreshCw, Loader2, CalendarDays, X, ExternalLink } from "lucide-react";
import AttachmentPanel from "@/components/AttachmentPanel";
import { Checkbox } from "@/components/ui/checkbox";
import { asArray } from "@/lib/asArray";
import { useRiskHeadFilter, RiskHeadChip, RiskHeadEmptyRow } from "@/components/RiskHeadFilter";   // PR 58 / 59 · F6: ?head=
import { RiskRatingSelect, RatingBadge, ratingsComplete } from "@/components/RiskRatingSelect";   // Silent defaults · A-26
import RiskFsHeadPicker from "@/components/RiskFsHeadPicker";   // SUNDAY BATCH · D-4: FS heads stored as codes (fs_codes)
import TbOnlyNotice from "@/components/TbOnlyNotice";   // PR 3 · rule 1 (B-1a): the cut-off scan needs journal lines
import { fmtNumber, fmtCount } from "@/components/Amount";   // STEP 3 · rule 2 · B-2e: the one amount formatter
import { jobErrorText } from "@/lib/jobErrorText";   // Step 11d · A2: a failed job's error in the screen's language

const FS_AREAS = ["Revenue", "Cost of Sales", "Payroll & Opex", "Cash & Bank",
                   "Receivables", "Payables", "Inventory", "Fixed Assets",
                   "Borrowings", "Equity", "Tax & VAT", "Journal Entries", "Other"];
const ASSERTIONS = ["Existence / Occurrence", "Completeness", "Accuracy",
                     "Valuation & Allocation", "Rights & Obligations",
                     "Presentation & Disclosure", "Cut-off"];


export default function RiskAssessment() {
  const colW2 = useResizableColumns({ storageKey: "risk-register", defaults: [240, 160, 240, 100, 100, 100, 240, 110, 100], mins: [130, 100, 130, 64, 64, 64, 130, 72, 60] });   // Step 11d · add-on
  const colW = useResizableColumns({ storageKey: "risk-assessment-risk-ai-drafts-table", defaults: [240, 160, 240, 160, 110, 110, 100], mins: [130, 100, 130, 100, 72, 72, 60] });   // Step 11d · add-on
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const { current } = useEngagement();
  // STEP 3 · B-8b: the server's own sets (GET /review-roles) — delete a risk = REVIEW_ROLES (DELETE /risk/items/{id});
  // Batch 31 · item 7 (M89, owner 18-Oct-2026): every team role adds / edits risks and takes suggestions (the server checks
  // engagement access and the lock). EQC is not a team role. Non-working days: any team member (engagement_locale).
  const { can } = useReviewRoles();
  const canEdit = can("risk.delete", user?.role);
  const canEditRisks = can("risk.edit", user?.role);
  const canEditNwd = can("engagement.locale", user?.role);
  const [tab, setTab] = useState("materiality");
  // Round M2 · A2 — the "fraud" sub-tab was removed from the tab
  // list; anyone landing here with `?tab=fraud` (old deep-link,
  // bookmarks, email links) is redirected to /fieldwork/a9001.
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  useEffect(() => {
    if (searchParams.get("tab") === "fraud") {
      navigate("/fieldwork/a9001", { replace: true });
    }
  }, [searchParams, navigate]);
  // Batch A1 — materiality is read-only here; 105(a) is the ONLY editor.
  const [matFigures, setMatFigures] = useState(null);
  const [risks, setRisks] = useState([]);
  const headFilter = useRiskHeadFilter(risks, lang);   // F6: register rows of ?head=<fs_code> (heat-map link)
  useEffect(() => { if (headFilter.head) setTab("general"); }, [headFilter.head]);   // the register lives on the General tab
  const [fraud, setFraud] = useState(null);
  const [fraudLoading, setFraudLoading] = useState(false);
  const [fraudError, setFraudError] = useState("");
  const [cutoff, setCutoff] = useState(null);
  const [cutoffStatus, setCutoffStatus] = useState("empty");
  const [cutoffGeneratedAt, setCutoffGeneratedAt] = useState("");
  const [cutoffScanning, setCutoffScanning] = useState(false);
  const [fraudStatus, setFraudStatus] = useState("empty");
  const [fraudGeneratedAt, setFraudGeneratedAt] = useState("");
  const [, setFraudScanning] = useState(false);
  // AbortController for in-flight /risk/* calls — cancelled on
  // engagement switch and on manual Refresh, so a slow fraud scan
  // from engagement A can't populate state after the user moved to B.
  const inflightRef = React.useRef(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editingRisk, setEditingRisk] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiDrafts, setAiDrafts] = useState([]);
  const [aiError, setAiError] = useState("");
  const [draftMode, setDraftMode] = useState("ai");
  const [editDraft, setEditDraft] = useState(null);

  const decideDraft = async (i, action, draft) => {
    const d = draft || aiDrafts[i];
    try {
      await api.post(`/risk/${current.id}/ai-draft-decision`, { action, draft: d });
      setAiDrafts((prev) => prev.filter((_, j) => j !== i));
      setEditDraft(null);
      if (action === "accept") { loadAll(); toast.success(t("risk.aiSuggestAccept")); }
    } catch (e) { toast.error(toErrMsg(e, "Failed")); }
  };
  const [newRisk, setNewRisk] = useState({
    fs_area: "Revenue", assertion: "", risk_description: "",
    inherent_risk: "", control_risk: "", likelihood: "", impact: "", overall_rating: "",   // A-26: Not assessed until rated
    planned_response: "", linked_procedure_refs: [],
    fs_codes: [],   // D-4: the picked FS heads; [] = not linked
  });

  const loadAll = async () => {
    if (!current) return;
    // AbortController cancels any in-flight /risk/* calls from a prior
    // engagement — otherwise a fraud scan that hits its 60s wall on
    // engagement A could populate state after the user switched to B.
    if (inflightRef.current) inflightRef.current.abort();
    const ac = new AbortController();
    inflightRef.current = ac;
    // 60s client-side timeout mirrors backend RISK_SCAN_WALL_TIMEOUT_SEC.
    // Backend hits its cap → we get the degraded-result payload; socket
    // dies (edge 5xx) → axios ECONNABORTED → existing rejection handler
    // renders the red banner + Refresh button.
    const risk = { signal: ac.signal, timeout: 60_000 };
    // Fire all four in PARALLEL and process results INDEPENDENTLY —
    // previously a single failing call (e.g. a 404 on the risk items
    // endpoint) aborted the whole chain via the awaited-sequence, so
    // Fraud Risk and Cut-off tabs stayed empty even though their own
    // endpoints were fine. Promise.allSettled + per-slot try/catch
    // guarantees each tab either loads its data OR shows an error
    // toast, never silently blanks.
    const eid = current.id;
    setFraudLoading(true);
    setFraudError("");
    const [, itemsRes, fraudRes, cutoffRes] = await Promise.allSettled([
      api.get(`/engagements/${eid}`, { signal: ac.signal }),
      api.get(`/risk/${eid}/items`, { signal: ac.signal }),
      api.get(`/risk/${eid}/fraud`, { ...risk, _silent: true }),
      api.get(`/risk/${eid}/cutoff?n=5`, risk),
    ]);
    setFraudLoading(false);

    // Slot 1 — materiality figures (Batch A1: read from 105(a) one source)
    try {
      const mf = await api.get(`/engagements/${eid}/materiality-figures`,
        { signal: ac.signal, _silent: true });
      setMatFigures(mf?.data || null);
    } catch { setMatFigures(null); }

    // Slot 2 — risk items
    if (itemsRes.status === "fulfilled") {
      const d = itemsRes.value?.data;
      setRisks(Array.isArray(d) ? d : []);
    }

    // Slot 3 — fraud fingerprints (shape-guard: only set when the
    // response is the expected object with a `groups` array; a stale
    // 404 that the interceptor coalesces to `[]` must NOT overwrite
    // state to a shape the render can't handle). On rejection we
    // capture the error so the tab renders an explicit failure state
    // with a Refresh button instead of blanking silently — the prior
    // fix guarded shape but still left the tab empty on network /
    // edge failures (e.g. Cloudflare 5xx), which reads as "the bug
    // is back" from the user's side.
    if (fraudRes.status === "fulfilled") {
      const d = fraudRes.value?.data;
      // V9 (Feb 2026): GET /risk/{id}/fraud now returns
      // { status, result, generated_at, ... }. Unwrap .result to keep
      // the render code that expects { groups, notes, population_size }
      // unchanged.
      const inner = d?.result;
      if (inner && !Array.isArray(inner) && Array.isArray(inner.groups)) {
        setFraud(inner);
        setFraudError("");
      } else {
        setFraud(null);
        if (d?.status === "empty") setFraudError("");
        else if (d?.status === "running") setFraudError("");
        else setFraudError("");
      }
      setFraudStatus(d?.status || "empty");
      setFraudGeneratedAt(d?.generated_at || "");
      setFraudScanning(d?.status === "running");
    } else {
      setFraud(null);
      setFraudError(toErrMsg(fraudRes.reason) || t("risk.toasts.fraudLoadFailed"));
    }

    // Slot 4 — cut-off (first/last, same cached-shape unwrap)
    if (cutoffRes.status === "fulfilled") {
      const d = cutoffRes.value?.data;
      const inner = d?.result;
      if (inner && !Array.isArray(inner) && (Array.isArray(inner.first) || Array.isArray(inner.last))) {
        setCutoff(inner);
      } else {
        setCutoff(null);
      }
      setCutoffStatus(d?.status || "empty");
      setCutoffGeneratedAt(d?.generated_at || "");
      setCutoffScanning(d?.status === "running");
    }
  };

  const loadAllOnSwitch = useEffectEvent(loadAll);
  useEffect(() => { loadAllOnSwitch(); }, [current?.id, loadAllOnSwitch]);

  // V9 (Feb 2026): Fraud scan is worker-only.
  //   POST /risk/{id}/fraud/scan  → enqueues an ARQ fraud_scan job.
  //   GET  /risk/{id}/fraud       → returns cached result + status.
  // The Refresh button enqueues, then polls the GET every 3s while
  // status === "running" so the tiles fill in without a page reload.
  const _pollTimerRef = React.useRef(null);
  React.useEffect(() => () => { if (_pollTimerRef.current) clearInterval(_pollTimerRef.current); }, []);

  const _pollFraud = () => {
    if (_pollTimerRef.current) clearInterval(_pollTimerRef.current);
    _pollTimerRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/risk/${current.id}/fraud`, { _silent: true });
        const d = r?.data;
        const inner = d?.result;
        if (inner && Array.isArray(inner.groups)) setFraud(inner);
        setFraudStatus(d?.status || "empty");
        setFraudGeneratedAt(d?.generated_at || "");
        if (d?.status !== "running") {
          setFraudScanning(false);
          clearInterval(_pollTimerRef.current);
          _pollTimerRef.current = null;
        }
      } catch { /* keep polling */ }
    }, 3000);
  };

  const refreshFraud = async () => {
    if (!current) return;
    setFraudLoading(true);
    setFraudError("");
    try {
      const r = await api.post(`/risk/${current.id}/fraud/scan`);
      if (r?.data?.status === "already_running") {
        toast.info(t("risk.toasts.fraudAlreadyRunning"));
      } else {
        toast.success(t("risk.toasts.fraudQueued"));
      }
      setFraudScanning(true);
      setFraudStatus("running");
      _pollFraud();
    } catch (e) {
      setFraudError(toErrMsg(e) || t("risk.toasts.fraudEnqueueFailed"));
    } finally {
      setFraudLoading(false);
    }
  };

  const _pollCutoff = () => {
    if (_pollTimerRef.current) clearInterval(_pollTimerRef.current);
    _pollTimerRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/risk/${current.id}/cutoff`, { _silent: true });
        const d = r?.data;
        const inner = d?.result;
        if (inner) setCutoff(inner);
        setCutoffStatus(d?.status || "empty");
        setCutoffGeneratedAt(d?.generated_at || "");
        if (d?.status !== "running") {
          setCutoffScanning(false);
          clearInterval(_pollTimerRef.current);
          _pollTimerRef.current = null;
        }
      } catch { /* keep polling */ }
    }, 3000);
  };

  const refreshCutoff = async () => {
    if (!current) return;
    setCutoffScanning(true);
    try {
      const r = await api.post(`/risk/${current.id}/cutoff/scan?n=5`);
      if (r?.data?.status === "already_running") {
        toast.info(t("risk.toasts.cutoffAlreadyRunning"));
      } else {
        toast.success(t("risk.toasts.cutoffQueued"));
      }
      setCutoffStatus("running");
      _pollCutoff();
    } catch (e) {
      setCutoffScanning(false);
      toast.error(toErrMsg(e) || t("risk.toasts.cutoffEnqueueFailed"));
    }
  };

  // Batch A1 — RA materiality save path is removed. 105(a) is the only
  // editor; the backend now returns 410 Gone on PATCH /risk/{id}/materiality.


  const addRisk = async () => {
    if (!current) return;
    if (!ratingsComplete(newRisk)) { toast.error(t("sd.ratingsRequired")); return; }   // A-26 (the server refuses it too)
    try {
      await api.post(`/risk/${current.id}/items`, newRisk);
      toast.success(t("risk.toasts.riskAdded"));
      setAddOpen(false);
      setNewRisk({ ...newRisk, risk_description: "", planned_response: "",
                       linked_procedure_refs: [], fs_codes: [] });
      await loadAll();
    } catch (e) { toast.error(toErrMsg(e, t("risk.toasts.addFailed"))); }
  };

  const removeRisk = async (id) => {
    try {
      await api.delete(`/risk/items/${id}`);
      setRisks((prev) => prev.filter((r) => r.id !== id));
    } catch { toast.error(t("risk.toasts.deleteFailed")); }
  };

  const saveEditedRisk = async () => {
    if (!editingRisk) return;
    if (!ratingsComplete(editingRisk)) { toast.error(t("sd.ratingsRequired")); return; }   // A-26
    try {
      const { id, ...body } = editingRisk;
      await api.patch(`/risk/items/${id}`, body);
      toast.success(t("risk.toasts.riskUpdated"));
      setEditingRisk(null);
      await loadAll();
    } catch (e) { toast.error(toErrMsg(e, t("risk.toasts.updateFailed"))); }
  };

  if (!current) return <div className="text-sm text-muted-foreground">{t("common.noEng")}</div>;

  return (
    <div className="space-y-6" data-testid="risk-page">
      <LockedBanner engagement={current} />
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{t("risk.title")}</h1>
        <p className="text-sm text-muted-foreground">{t("risk.subtitle")}</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="materiality" data-testid="tab-mat">{t("risk.tabs.materiality")}</TabsTrigger>
          <TabsTrigger value="general" data-testid="tab-general">{t("risk.tabs.general")}</TabsTrigger>
          {/* Round M2 · A2 — Fraud Risk (ISA 240) sub-tab REMOVED
              from the tab list. Code below (state, TabsContent) is
              intentionally kept per R7 — nothing hard-deleted — so
              the old data path still resolves and history stays
              intact. Auditors reach the merged tab via the sidebar
              pointer "Fraud red flags (ISA 240) → A9001" or the old
              deep-link `/risk?tab=fraud` which now redirects. */}
        </TabsList>

        <TabsContent value="materiality">
          <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-3">
              <CardTitle className="text-base flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-emerald-600" /> {t("risk.materialityTitle")}
              </CardTitle>
              <Link to="/planning?openForm=materiality"
                    className="inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50/40 px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/20 dark:text-emerald-300"
                    data-testid="risk-mat-edit-in-105a">
                {t("risk.materialityEditIn105a")} <ExternalLink className="h-3 w-3" />
              </Link>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-xs text-muted-foreground">{t("risk.materialityReadOnlyNote")}</p>
              {(() => {
                const mf = matFigures || {};
                const unset = !mf.benchmark
                              && !Number(mf.overall_materiality || 0)
                              && !Number(mf.performance_materiality || 0);
                if (unset) {
                  return (
                    <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300"
                         data-testid="risk-mat-unset">
                      {t("risk.materialityNotSet")}
                    </div>
                  );
                }
                const fmt = (n) => fmtNumber(Number(n || 0));
                return (
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3" data-testid="risk-mat-readonly">
                    <div>
                      <Label>{t("risk.overall")}</Label>
                      <div className="mt-1 rounded-md border bg-slate-50 px-3 py-2 font-mono text-sm dark:bg-slate-900/40"
                           data-testid="mat-overall">
                        {fmt(mf.overall_materiality)}
                      </div>
                    </div>
                    <div>
                      <Label>{t("risk.performance")}</Label>
                      <div className="mt-1 rounded-md border bg-slate-50 px-3 py-2 font-mono text-sm dark:bg-slate-900/40"
                           data-testid="mat-perf">
                        {fmt(mf.performance_materiality)}
                      </div>
                    </div>
                    <div>
                      <Label>{t("risk.clearlyTrivial")}</Label>
                      <div className="mt-1 rounded-md border bg-slate-50 px-3 py-2 font-mono text-sm dark:bg-slate-900/40"
                           data-testid="mat-ct">
                        {fmt(mf.clearly_trivial)}
                      </div>
                      <CtNotSetNote value={mf.clearly_trivial} testid="risk-ct-not-set" className="mt-1" />
                    </div>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="general">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">{t("risk.generalTitle")}</CardTitle>
              {canEditRisks && (
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline"
                          onClick={async () => {
                            if (!current?.id) return;
                            setAiBusy(true); setAiError(""); setDraftMode("ai");
                            try {
                              const r = await api.post(`/risk/${current.id}/ai-suggest`, {}, { _silent: true });
                              const jobId = r.data?.job_id;
                              toast.info(t("risk.aiSuggestQueued"));
                              let job = null;
                              for (let i = 0; i < 90; i++) {
                                await new Promise((res) => setTimeout(res, 2000));
                                job = (await api.get(`/jobs/${jobId}`)).data;
                                if (job.status === "done" || job.status === "failed") break;
                              }
                              if (!job || job.status !== "done") throw new Error(jobErrorText(job, lang, t, t("risk.aiSuggestFailedMsg")));
                              const res = job.result || {};
                              setAiDrafts(res.drafts || []);
                              if (res.empty_tb) { setAiError(t("risk.aiSuggestEmptyTb")); toast.info(t("risk.aiSuggestEmptyTb")); }
                              else if (!res.drafts?.length) toast.info(t("risk.aiSuggestEmpty"));
                            } catch (e) {
                              // 402 (AI allocation) is shown once by the global gate modal — no extra toast/banner.
                              if (e?.response?.status !== 402) setAiError(toErrMsg(e, t("risk.aiSuggestFailedMsg")));
                            } finally { setAiBusy(false); }
                          }}
                          disabled={aiBusy}
                          data-testid="risk-ai-suggest-btn">
                    {aiBusy ? t("risk.aiSuggestLoading") : t("risk.aiSuggestBtn")}
                  </Button>
                  <Button size="sm" variant="ghost"
                          onClick={async () => {
                            if (!current?.id) return;
                            setAiError(""); setDraftMode("library");
                            try {
                              const r = await api.get(`/risk/${current.id}/risk-library`);
                              setAiDrafts(r.data?.drafts || []);
                            } catch (e) { toast.error(toErrMsg(e, "Failed")); }
                          }}
                          disabled={aiBusy}
                          data-testid="risk-library-btn">
                    {t("risk.riskLibraryBtn")}
                  </Button>
                  {/* Batch 10 · B13: every new risk starts with NO linked procedures — refs picked in a cancelled add no longer ride
                      along (invisible) into the next risk, e.g. G14.01 (revenue vouching) onto a Journal Entries risk */}
                  <Dialog open={addOpen} onOpenChange={(v) => { if (v) setNewRisk((n) => ({ ...n, linked_procedure_refs: [] })); setAddOpen(v); }}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="bg-slate-900 hover:bg-slate-800"
                            data-testid="risk-add-btn">
                      <Plus className="me-1 h-3 w-3" /> {t("risk.addRisk")}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-xl">
                    <DialogHeader><DialogTitle>{t("risk.addRisk")}</DialogTitle></DialogHeader>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <Label>{t("risk.fsArea")}</Label>
                        <Select value={newRisk.fs_area} onValueChange={(v) => setNewRisk({ ...newRisk, fs_area: v, linked_procedure_refs: v === newRisk.fs_area ? (newRisk.linked_procedure_refs || []) : [] })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {FS_AREAS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>{t("risk.assertion")}</Label>
                        <Select value={newRisk.assertion} onValueChange={(v) => setNewRisk({ ...newRisk, assertion: v })}>
                          <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">—</SelectItem>
                            {ASSERTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Label>{t("risk.riskDesc")}</Label>
                        <Textarea rows={2} value={newRisk.risk_description}
                                  onChange={(e) => setNewRisk({ ...newRisk, risk_description: e.target.value })} />
                      </div>
                      <div className="col-span-2">
                        <RiskFsHeadPicker value={newRisk.fs_codes || []} onChange={(c) => setNewRisk({ ...newRisk, fs_codes: c })}
                                          t={t} lang={lang} testidPrefix="risk-add-fs" />
                      </div>
                      {["inherent_risk", "control_risk", "likelihood", "impact", "overall_rating"].map((k) => (
                        <div key={k}>
                          <Label>{k.replace(/_/g, " ")}</Label>
                          <RiskRatingSelect value={newRisk[k]} onValueChange={(v) => setNewRisk({ ...newRisk, [k]: v })}
                                            t={t} testid={`risk-add-${k}`} />
                        </div>
                      ))}
                      <div className="col-span-2">
                        <Label>{t("risk.response")}</Label>
                        <Textarea rows={2} value={newRisk.planned_response}
                                  onChange={(e) => setNewRisk({ ...newRisk, planned_response: e.target.value })} />
                      </div>
                      <div className="col-span-2">
                        <ProcedureLinker engagementId={current?.id}
                                           fsArea={newRisk.fs_area}
                                           value={newRisk.linked_procedure_refs || []}
                                           onChange={(refs) => setNewRisk({ ...newRisk, linked_procedure_refs: refs })}
                                           testidPrefix="risk-add" t={t} />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => { setNewRisk((n) => ({ ...n, linked_procedure_refs: [] })); setAddOpen(false); }}>{t("common.cancel")}</Button>
                      <Button onClick={addRisk} data-testid="risk-save-btn" className="bg-slate-900 hover:bg-slate-800">{t("risk.saveRisk")}</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
                </div>
              )}
            </CardHeader>
            {aiError && aiDrafts.length === 0 && (
              <div className="mx-4 mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900"
                   data-testid="risk-ai-error">
                {aiError}
              </div>
            )}
            {aiDrafts.length > 0 && (
              <div className="mx-4 mt-3 rounded-md border border-sky-200 bg-sky-50/40 p-3"
                   data-testid="risk-ai-drafts-panel">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <div className="text-xs text-sky-900">
                    {draftMode === "library" ? t("risk.riskLibraryHint") : t("risk.aiSuggestHint")}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline"
                            onClick={() => { setAiDrafts([]); }}
                            data-testid="risk-ai-discard-all-btn">
                      {t("risk.aiSuggestDiscard")}
                    </Button>
                    <Button size="sm"
                            onClick={async () => {
                              let n = 0;
                              for (const d of aiDrafts) {
                                try { await api.post(`/risk/${current.id}/ai-draft-decision`, { action: "accept", draft: d }); n += 1; }
                                catch (_e) { /* ignore per-row */ }
                              }
                              setAiDrafts([]);
                              loadAll();
                              toast.success(`${n} risks added`);
                            }}
                            data-testid="risk-ai-accept-all-btn"
                            className="bg-emerald-600 hover:bg-emerald-700 text-white">
                      {t("risk.aiSuggestAcceptAll")}
                    </Button>
                  </div>
                </div>
                <div className="overflow-auto rounded border bg-white">
                  <ResizableTable cols={colW} className="w-full text-xs" data-testid="risk-ai-drafts-table">
                    <thead className="border-b bg-slate-50 text-[10px] uppercase text-muted-foreground">
                      <tr>
                        <ResizableTh index={0} className="px-2 py-1.5 text-start">{t("risk.fsArea")}</ResizableTh>
                        <ResizableTh index={1} className="px-2 py-1.5 text-start">{t("risk.assertion")}</ResizableTh>
                        <ResizableTh index={2} className="px-2 py-1.5 text-start">{t("risk.riskDesc")}</ResizableTh>
                        <ResizableTh index={3} className="px-2 py-1.5 text-start">{t("risk.overallRating")}</ResizableTh>
                        <ResizableTh index={4} className="px-2 py-1.5 text-start">{t("risk.aiSuggestSource")}</ResizableTh>
                        <ResizableTh index={5} className="px-2 py-1.5 text-start">{t("risk.aiSuggestStatus")}</ResizableTh>
                        <ResizableTh index={6} className="px-2 py-1.5 text-end"></ResizableTh>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {aiDrafts.map((d, i) => (
                        <tr key={i} data-testid={`risk-ai-draft-${i}`}>
                          <td className="px-2 py-1.5 font-medium">{d.fs_area}</td>
                          <td className="px-2 py-1.5 text-muted-foreground">{d.assertion || "—"}</td>
                          <td className="px-2 py-1.5">{d.risk_description}</td>
                          <td className="px-2 py-1.5"><RatingBadge v={d.overall_rating} t={t} /></td>
                          <td className="px-2 py-1.5 text-[11px]" data-testid={`risk-ai-draft-source-${i}`}>
                            {d.source?.href
                              ? <Link to={d.source.href} className="text-sky-700 underline underline-offset-2">{d.source.label}</Link>
                              : (d.source?.label || "—")}
                          </td>
                          <td className="px-2 py-1.5">
                            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px]">{d.status || "AI draft"}</span>
                          </td>
                          <td className="px-2 py-1.5 text-end whitespace-nowrap space-x-1">
                            <Button size="sm" variant="outline"
                                    data-testid={`risk-ai-accept-${i}`}
                                    onClick={() => decideDraft(i, "accept")}>
                              {t("risk.aiSuggestAccept")}
                            </Button>
                            <Button size="sm" variant="ghost"
                                    data-testid={`risk-ai-edit-${i}`}
                                    onClick={() => setEditDraft({ idx: i, d: { ...d } })}>
                              {t("risk.aiSuggestEdit")}
                            </Button>
                            <Button size="sm" variant="ghost" className="text-rose-700"
                                    data-testid={`risk-ai-reject-${i}`}
                                    onClick={() => decideDraft(i, "reject")}>
                              {t("risk.aiSuggestReject")}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </ResizableTable>
                </div>
                {editDraft && (
                  <Dialog open onOpenChange={(o) => { if (!o) setEditDraft(null); }}>
                    <DialogContent className="max-w-xl" data-testid="risk-ai-edit-dialog">
                      <DialogHeader><DialogTitle>{t("risk.aiSuggestEditTitle")}</DialogTitle></DialogHeader>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="col-span-2">
                          <Label>{t("risk.riskDesc")}</Label>
                          <Textarea rows={3} value={editDraft.d.risk_description || ""}
                                    data-testid="risk-ai-edit-desc"
                                    onChange={(e) => setEditDraft({ ...editDraft, d: { ...editDraft.d, risk_description: e.target.value, edited: true } })} />
                        </div>
                        <div>
                          <Label>{t("risk.assertion")}</Label>
                          <Input value={editDraft.d.assertion || ""}
                                 onChange={(e) => setEditDraft({ ...editDraft, d: { ...editDraft.d, assertion: e.target.value, edited: true } })} />
                        </div>
                        {["likelihood", "impact", "overall_rating"].map((k) => (
                          <div key={k}>
                            <Label>{k.replace(/_/g, " ")}</Label>
                            <RiskRatingSelect value={editDraft.d[k]}
                                              onValueChange={(v) => setEditDraft({ ...editDraft, d: { ...editDraft.d, [k]: v, edited: true } })}
                                              t={t} testid={`risk-ai-edit-${k}`} />
                          </div>
                        ))}
                        <div className="col-span-2">
                          <Label>{t("risk.response")}</Label>
                          <Textarea rows={2} value={editDraft.d.planned_response || ""}
                                    onChange={(e) => setEditDraft({ ...editDraft, d: { ...editDraft.d, planned_response: e.target.value, edited: true } })} />
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setEditDraft(null)}>{t("common.cancel")}</Button>
                        <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="risk-ai-edit-accept-btn"
                                onClick={() => decideDraft(editDraft.idx, "accept", editDraft.d)}>
                          {t("risk.aiSuggestAcceptSave")}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                )}
                {aiError && (
                  <div className="mt-2 text-[11px] text-rose-700" data-testid="risk-ai-error-inline">
                    {aiError}
                  </div>
                )}
              </div>
            )}
            <CardContent className="p-0">
              <RiskHeadChip {...headFilter} t={t} />
              <ResizableTable cols={colW2} wrap className="w-full text-sm">
                <thead className="border-b bg-slate-50 text-xs uppercase text-muted-foreground">
                  <tr>
                    <ResizableTh index={0} className="px-3 py-2 text-start">{t("risk.fsArea")}</ResizableTh>
                    <ResizableTh index={1} className="px-3 py-2 text-start">{t("risk.assertion")}</ResizableTh>
                    <ResizableTh index={2} className="px-3 py-2 text-start">{t("risk.riskDesc")}</ResizableTh>
                    <ResizableTh index={3} className="px-3 py-2 text-center">{t("risk.inherent")}</ResizableTh>
                    <ResizableTh index={4} className="px-3 py-2 text-center">{t("risk.control")}</ResizableTh>
                    <ResizableTh index={5} className="px-3 py-2 text-center">{t("risk.overallRating")}</ResizableTh>
                    <ResizableTh index={6} className="px-3 py-2 text-start">{t("risk.response")}</ResizableTh>
                    <ResizableTh index={7} className="px-3 py-2 text-start">{t("risk.refColLinked")}</ResizableTh>
                    <ResizableTh index={8} className="px-3 py-2 text-end"></ResizableTh>
                  </tr>
                </thead>
                <tbody>
                  {risks.length === 0 && (
                    <tr><td colSpan={9} className="px-3 py-6 text-center text-muted-foreground">
                      {t("risk.tableEmpty")}
                    </td></tr>
                  )}
                  <RiskHeadEmptyRow head={headFilter.head} shown={headFilter.rows.length} total={risks.length} colSpan={9} t={t} />
                  {headFilter.rows.map((r) => (
                    <tr key={r.id} className="border-b" data-testid={`risk-row-${r.id}`}>
                      <td className="px-3 py-2 font-medium">{r.fs_area}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{r.assertion || "—"}</td>
                      <td className="px-3 py-2">{r.risk_description}
                        {r.carried_forward && (   /* Batch 10.1 · R2 */
                          <span className="ms-1 rounded bg-sky-100 px-1 py-0.5 text-[10px] text-sky-800 dark:bg-sky-950/40 dark:text-sky-200"
                                data-testid={`risk-carried-${r.id}`}>{t("common.carriedForward")}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center"><RatingBadge v={r.inherent_risk} t={t} /></td>
                      <td className="px-3 py-2 text-center"><RatingBadge v={r.control_risk} t={t} /></td>
                      <td className="px-3 py-2 text-center"><RatingBadge v={r.overall_rating} t={t} /></td>
                      <td className="px-3 py-2 text-xs">{r.planned_response}</td>
                      <td className="px-3 py-2 text-xs" data-testid={`risk-linked-${r.id}`}>
                        {(r.linked_procedure_refs || []).length === 0
                          ? <span className="text-muted-foreground">—</span>
                          : asArray(r.linked_procedure_refs).map((ref) => (
                              <span key={ref}
                                    className="me-1 mb-1 inline-block rounded border bg-emerald-50 px-1.5 py-0.5 font-mono text-[10px] text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300"
                                    data-testid={`risk-linked-${r.id}-${ref}`}>
                                {`\u2068${ref}\u2069`}
                              </span>
                            ))}
                      </td>
                      <td className="px-3 py-2 text-end space-x-1">
                        {canEditRisks && (
                          <Button size="sm" variant="outline"
                                  onClick={() => setEditingRisk({ ...r })}
                                  data-testid={`risk-edit-${r.id}`}>
                            {t("risk.edit")}
                          </Button>
                        )}
                        {canEdit && (
                          <Button size="sm" variant="ghost" className="text-red-600"
                                  onClick={() => removeRisk(r.id)} data-testid={`risk-del-${r.id}`}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </ResizableTable>
            </CardContent>
          </Card>

          {/* Edit risk dialog */}
          <Dialog open={!!editingRisk} onOpenChange={(v) => !v && setEditingRisk(null)}>
            <DialogContent className="max-w-xl">
              <DialogHeader><DialogTitle>{t("risk.editRisk")}</DialogTitle></DialogHeader>
              {editingRisk && (
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <Label>{t("risk.fsArea")}</Label>
                    <Select value={editingRisk.fs_area}
                            onValueChange={(v) => setEditingRisk({ ...editingRisk, fs_area: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {FS_AREAS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{t("risk.assertion")}</Label>
                    <Select value={editingRisk.assertion || "__none__"}
                            onValueChange={(v) => setEditingRisk({ ...editingRisk, assertion: v === "__none__" ? "" : v })}>
                      <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">—</SelectItem>
                        {ASSERTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Label>{t("risk.riskDesc")}</Label>
                    <Textarea rows={2} value={editingRisk.risk_description || ""}
                              onChange={(e) => setEditingRisk({ ...editingRisk, risk_description: e.target.value })} />
                  </div>
                  <div className="col-span-2">
                    <RiskFsHeadPicker value={Array.isArray(editingRisk.fs_codes) ? editingRisk.fs_codes : null}
                                      fallback={asArray(editingRisk.fs_heads).map((h) => h.fs_code)}
                                      onChange={(c) => setEditingRisk({ ...editingRisk, fs_codes: c })}
                                      t={t} lang={lang} testidPrefix="risk-edit-fs" />
                  </div>
                  {["inherent_risk", "control_risk", "likelihood", "impact", "overall_rating"].map((k) => (
                    <div key={k}>
                      <Label>{k.replace(/_/g, " ")}</Label>
                      <RiskRatingSelect value={editingRisk[k]} onValueChange={(v) => setEditingRisk({ ...editingRisk, [k]: v })}
                                        t={t} testid={`risk-edit-${k}`} />
                    </div>
                  ))}
                  <div className="col-span-2">
                    <Label>{t("risk.response")}</Label>
                    <Textarea rows={2} value={editingRisk.planned_response || ""}
                              onChange={(e) => setEditingRisk({ ...editingRisk, planned_response: e.target.value })} />
                  </div>
                  <div className="col-span-2">
                    <ProcedureLinker engagementId={current?.id}
                                       fsArea={editingRisk.fs_area}
                                       value={editingRisk.linked_procedure_refs || []}
                                       onChange={(refs) => setEditingRisk({ ...editingRisk, linked_procedure_refs: refs })}
                                       testidPrefix="risk-edit" t={t} />
                  </div>
                  <div className="col-span-2">
                    <AttachmentPanel engagementId={current.id}
                                       contextType="risk" contextId={editingRisk.id}
                                       locked={!!current.signed_off}
                                       label={t("risk.attachLabel")} />
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditingRisk(null)}>{t("common.cancel")}</Button>
                <Button onClick={saveEditedRisk} className="bg-slate-900 hover:bg-slate-800"
                        data-testid="risk-edit-save">{t("risk.saveChanges")}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="fraud">
          <NonWorkingDaysCard engagementId={current?.id}
                              canEdit={canEditNwd && !current?.signed_off}
                              onSaved={() => { /* next Refresh applies it */ }} />
          <Card className="mt-4">
            <CardHeader>
              <div className="flex items-start justify-between gap-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600" /> {t("risk.fraudTitle")}
                </CardTitle>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={refreshFraud}
                  disabled={fraudLoading || !current}
                  data-testid="fraud-refresh-btn"
                  className="h-8 shrink-0"
                >
                  {fraudLoading
                    ? <><Loader2 className="me-1.5 h-3.5 w-3.5 animate-spin" /> {t("risk.refreshing")}</>
                    : <><RefreshCw className="me-1.5 h-3.5 w-3.5" /> {t("risk.refresh")}</>}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-xs text-muted-foreground">{t("risk.fraudDesc")}</p>
              {/* Batch 27 · Add-on 2 Step 2: the shared run-state banner (the Refresh button above runs it) */}
              <StoredResultBanner lang={lang} canRun={false} testid="fraud-run-state"
                                  state={fraudStatus === "empty" ? "not_run" : fraudStatus === "stale" ? "stale" : "fresh"}
                                  runAt={fraudGeneratedAt} running={fraudLoading || fraudStatus === "running"} />

              {/* Failure state — visible red banner + retry hint. Previously
                  a rejected /risk/{id}/fraud call left `fraud=null` and the
                  tab silently blanked, reading as "the fix regressed" from
                  the user side. */}
              {!fraudLoading && fraudError && (
                <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-900 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200"
                     data-testid="fraud-error">
                  <div className="mb-1 font-semibold uppercase tracking-wider text-[10px]">{t("risk.fraudLoadError")}</div>
                  <div>{fraudError}</div>
                  <div className="mt-1 text-[11px] opacity-80">
                    {t("risk.fraudRetryHint").split("{refreshBold}").map((chunk, i, arr) => (
                      <React.Fragment key={i}>
                        {chunk}
                        {i < arr.length - 1 && <b>{t("risk.refreshBold")}</b>}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              )}

              {/* Loading state — only shown when we have nothing to display yet */}
              {fraudLoading && !fraud && !fraudError && (
                <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700 dark:border-slate-800 dark:bg-slate-900/40 dark:text-slate-300"
                     data-testid="fraud-loading">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  {t("risk.fraudRunning")}
                </div>
              )}

              {/* Empty-state hint when scan succeeded but the engagement
                  has no current-period GL rows yet. Distinct from a
                  failed request so the user knows to import GL data,
                  not to retry. */}
              {!fraudLoading && !fraudError && fraud
                && typeof fraud.population_size === "number"
                && fraud.population_size === 0 && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200"
                     data-testid="fraud-empty">
                  <div className="mb-1 font-semibold uppercase tracking-wider text-[10px]">{t("risk.fraudEmptyTitle")}</div>
                  <div>
                    {t("risk.fraudEmptyBody").split("{linkItalic}").map((chunk, i, arr) => (
                      <React.Fragment key={i}>
                        {chunk}
                        {i < arr.length - 1 && <i>{t("risk.fraudEmptyLink")}</i>}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              )}

              {fraud && Array.isArray(fraud.notes) && fraud.notes.length > 0 && (
                <div className="rounded-md border border-sky-200 bg-sky-50 p-3 text-xs text-sky-900 dark:border-sky-900 dark:bg-sky-950/30 dark:text-sky-200"
                     data-testid="fraud-notes">
                  <div className="mb-1 font-semibold uppercase tracking-wider text-[10px]">{t("risk.dataQualityNotes")}</div>
                  <ul className="list-disc space-y-1 ps-5">
                    {fraud.notes.map((n, i) => <li key={i}>{n}</li>)}
                  </ul>
                </div>
              )}
              {fraud && typeof fraud.population_size === "number" && fraud.population_size > 0 && (
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground" data-testid="fraud-population-size">
                  {t("risk.populationLabel")} <span className="font-semibold text-foreground tabular-nums">{fmtCount(fraud.population_size)}</span> {t("risk.populationSuffix")}
                </div>
              )}
              {fraud && Array.isArray(fraud.groups) && fraud.population_size > 0 && (
                <>
                  {fraud.groups.some((g) => g.hits_truncated) && (
                    <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200"
                         data-testid="fraud-truncated-banner">
                      <b>{t("risk.largeResultSetBold")}</b> {t("risk.largeResultSetBody").replace("{n}", fraud.groups.find((g) => g.hits_truncated)?.hits_head_size || 500)}
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
                    {fraud.groups.map((g) => (
                      <div key={g.label}
                           className={`rounded-md border p-3 ${g.count > 0 ? "border-amber-200 bg-amber-50/60 dark:bg-amber-950/20 dark:border-amber-900" : "border-slate-200 bg-slate-50 dark:bg-slate-900"}`}
                           data-testid={`fraud-tile-${g.label}`}>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{g.label}</div>
                        <div className="mt-1 flex items-center gap-2">
                          <div className="text-2xl font-bold tabular-nums">{fmtCount(g.count)}</div>
                          {g.capped && (
                            <span title={t("risk.cappedTitle")}
                                  className="rounded-full border border-red-300 bg-red-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-red-800 dark:border-red-800 dark:bg-red-950/60 dark:text-red-200"
                                  data-testid={`fraud-cap-${g.label}`}>
                              {t("risk.cappedBadge")}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="rounded-md border overflow-x-auto">
                    {fraud.groups.map((g) => (
                      g.count > 0 && (
                        <FraudGroup key={g.label} group={g} engId={current?.id} />
                      )
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="cutoff">
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-3">
                <CardTitle className="text-base">{t("risk.cutoffCardTitle")}</CardTitle>
                <Button type="button" variant="outline" size="sm"
                        onClick={refreshCutoff} disabled={cutoffScanning || !current}
                        data-testid="cutoff-refresh-btn"
                        className="h-8 shrink-0">
                  {cutoffScanning
                    ? <><Loader2 className="me-1.5 h-3.5 w-3.5 animate-spin" /> {t("risk.scanning")}</>
                    : <><RefreshCw className="me-1.5 h-3.5 w-3.5" /> {t("risk.refresh")}</>}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Batch 27 · Add-on 2 Step 2: the shared run-state banner (the Refresh button above runs it) */}
              <StoredResultBanner lang={lang} canRun={false} testid="cutoff-status"
                                  state={cutoffStatus === "empty" ? "not_run" : cutoffStatus === "stale" ? "stale" : "fresh"}
                                  runAt={cutoffGeneratedAt} running={cutoffScanning || cutoffStatus === "running"} />
              {cutoffScanning && (
                <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700 dark:border-slate-800 dark:bg-slate-900/40 dark:text-slate-300">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  {t("risk.cutoffRunning")}
                </div>
              )}
              <p className="text-xs text-muted-foreground">{t("risk.cutoffDesc")}</p>
              <TbOnlyNotice status={cutoff} testid="risk-cutoff-tb-only" />
              {cutoff && (Array.isArray(cutoff.first) || Array.isArray(cutoff.last)) && (
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  <CutoffTable title={t("risk.first")} rows={cutoff.first || []} />
                  <CutoffTable title={t("risk.last")} rows={cutoff.last || []} />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function FraudGroup({ group, engId }) {
  const colW3 = useResizableColumns({ storageKey: "risk-fraud-group", defaults: [130, 110, 130, 240], mins: [96, 72, 90, 130] });   // Step 11d · add-on
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const exportXlsx = async (e) => {
    e.stopPropagation();
    if (!engId) { toast.error(t("risk.selectEngFirst")); return; }
    setExporting(true);
    try {
      const resp = await api.get(
        `/risk/${engId}/fraud/export?rule=${encodeURIComponent(group.label)}`,
        { responseType: "blob", _silent: true });
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      const safe = group.label.replace(/[^a-z0-9]+/gi, "_").toLowerCase();
      a.download = `fraud_${safe}_${(engId || "").slice(0, 8)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t("risk.exportSuccess").replace("{n}", fmtCount(group.count)));
    } catch (err) {
      toast.error(toErrMsg(err, t("risk.exportFailed")));
    } finally { setExporting(false); }
  };
  return (
    <div className="border-b last:border-b-0">
      <div className="flex w-full items-center gap-2 px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-900/50">
        <button onClick={() => setOpen((o) => !o)}
                data-testid={`fraud-group-${group.label}`}
                className="flex flex-1 items-center justify-between text-start">
          <div className="text-sm font-medium">{group.label}</div>
          <div className="flex items-center gap-2">
            <span className="rounded-md bg-amber-100 px-2 py-0.5 text-xs text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">{group.count}</span>
            <ChevronsUpDown className="h-3 w-3 text-muted-foreground" />
          </div>
        </button>
        {group.count > 0 && (
          <>
            <Button size="sm" variant="outline"
                     className="h-7 border-emerald-200 bg-emerald-50 px-2 text-[11px] text-emerald-800 hover:bg-emerald-100 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200"
                     disabled={exporting}
                     onClick={exportXlsx}
                     data-testid={`fraud-export-${group.label}`}
                     title={t("risk.downloadXlsxTitle").replace("{n}", fmtCount(group.count))}>
              <Download className="me-1 h-3 w-3" />
              {exporting ? "…" : "XLSX"}
            </Button>
            {/* Navigation shortcut: pre-selects Continuous method + this
                 specific fingerprint on the Sampling tab. Detection logic
                 stays here — Sampling only consumes the flagged transactions. */}
            <Link to={`/sampling?method=continuous&rule=${encodeURIComponent(group.label)}`}
                   className="inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700 hover:bg-emerald-100 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300"
                   data-testid={`fraud-test-now-${group.label}`}
                   title={t("risk.testTheseNowTitle")}>
              <Play className="h-2.5 w-2.5" />
              {t("risk.testTheseNow")}
            </Link>
          </>
        )}
      </div>
      {open && (
        <>
          <div className="border-t bg-slate-50 px-3 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground dark:bg-slate-900/60"
               data-testid={`fraud-hits-count-${group.label}`}>
            {t("risk.showingPre")} <span className="font-semibold text-foreground tabular-nums">{fmtCount(group.hits.length)}</span> {t("risk.showingMid")} <span className="font-semibold text-foreground tabular-nums">{fmtCount(group.count)}</span> {t("risk.showingSuffix")}
            {group.hits.length < group.count && (
              <span className="ms-2 text-amber-700 dark:text-amber-300">{t("risk.truncatedByServer")}</span>
            )}
          </div>
          <div className="max-h-[520px] overflow-y-auto">
            <ResizableTable cols={colW3} className="w-full text-xs">
              <thead className="sticky top-0 z-10 border-t bg-slate-50 text-[10px] uppercase text-muted-foreground dark:bg-slate-900/80">
                <tr>
                  <ResizableTh index={0} className="px-3 py-1.5 text-start">{t("risk.colDate")}</ResizableTh>
                  <ResizableTh index={1} className="px-3 py-1.5 text-start">{t("risk.colVoucherAccount")}</ResizableTh>
                  <ResizableTh index={2} className="px-3 py-1.5 text-end">{t("risk.colAmount")}</ResizableTh>
                  <ResizableTh index={3} className="px-3 py-1.5 text-start">{t("risk.colReason")}</ResizableTh>
                </tr>
              </thead>
              <tbody>
                {group.hits.map((h, i) => (
                  <tr key={i} className="border-t">
                    <td className="px-3 py-1.5">{fmtDate(h.entry_date)}</td>
                    <td className="px-3 py-1.5">
                      {h.entry_id ? (
                        <a href={`/gl-entry/${h.entry_id}`}
                            target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1 rounded bg-emerald-50 px-1.5 py-0.5 font-mono text-[11px] text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-300"
                            data-testid={`fraud-voucher-link-${h.entry_id}`}
                            title={t("risk.openGlEntryTitle")}>
                          {h.voucher_no || t("risk.noVoucher")}
                        </a>
                      ) : (
                        h.voucher_no && <span className="me-1 font-mono">{h.voucher_no}</span>
                      )}
                      <span className="ms-1">{h.account_name}</span>
                    </td>
                    <td className="px-3 py-1.5 num-cell">{fmtNumber(Number(h.amount || 0))}</td>
                    <td className="px-3 py-1.5 text-muted-foreground">{h.reason}</td>
                  </tr>
                ))}
              </tbody>
            </ResizableTable>
          </div>
        </>
      )}
    </div>
  );
}

function CutoffTable({ title, rows }) {
  const colW4 = useResizableColumns({ storageKey: "risk-cutoff", defaults: [130, 110, 110, 130], mins: [96, 72, 72, 90] });   // Step 11d · add-on
  const { t } = useI18n();
  return (
    <div className="rounded-md border">
      <div className="border-b bg-slate-50 px-3 py-2 text-xs font-medium uppercase tracking-wider text-muted-foreground dark:bg-slate-900/60">{title}</div>
      <ResizableTable cols={colW4} wrap className="w-full text-sm">
        <thead className="border-b bg-slate-50 text-[10px] uppercase text-muted-foreground">
          <tr>
            <ResizableTh index={0} className="px-3 py-1.5 text-start">{t("risk.colDate")}</ResizableTh>
            <ResizableTh index={1} className="px-3 py-1.5 text-start">{t("risk.colVoucher")}</ResizableTh>
            <ResizableTh index={2} className="px-3 py-1.5 text-start">{t("risk.colAccount")}</ResizableTh>
            <ResizableTh index={3} className="px-3 py-1.5 text-end">{t("risk.colAmount")}</ResizableTh>
          </tr>
        </thead>
        <tbody>
          {asArray(rows).map((r, i) => {
            const amt = Math.abs((r.debit || 0) - (r.credit || 0));
            return (
              <tr key={i} className="border-b">
                <td className="px-3 py-1.5">{fmtDate(r.entry_date)}</td>
                <td className="px-3 py-1.5 font-mono text-xs">{r.voucher_no || "—"}</td>
                <td className="px-3 py-1.5 text-xs">{r.account_name}</td>
                <td className="px-3 py-1.5 num-cell">{fmtNumber(Number(amt || 0))}</td>
              </tr>
            );
          })}
        </tbody>
      </ResizableTable>
    </div>
  );
}

// -------- Non-working-days config card --------
// Powers the ISA 240 Fraud Risk "Weekend / after-hours posting" fingerprint,
// the Analytics weekend-flags block, and the Sampling risk-based scorer.
// Default weekdays are [5, 6] (Sat/Sun). Auditor may tick any day + add
// public holidays. Applies to the NEXT refresh (historical scans stay).
const WEEKDAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

function NonWorkingDaysCard({ engagementId, canEdit, onSaved }) {
  const { t } = useI18n();
  const [cfg, setCfg] = React.useState({ weekdays: [5, 6], holidays: [] });
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [newHoliday, setNewHoliday] = React.useState("");

  React.useEffect(() => {
    if (!engagementId) return;
    setLoading(true);
    api.get(`/engagements/${engagementId}/non-working-days`)
      .then((r) => {
        const nwd = r.data?.non_working_days || {};
        setCfg({
          weekdays: Array.isArray(nwd.weekdays) ? nwd.weekdays : [5, 6],
          holidays: Array.isArray(nwd.holidays) ? nwd.holidays : [],
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [engagementId]);

  const toggleDay = (d) => {
    if (!canEdit) return;
    const set = new Set(cfg.weekdays);
    if (set.has(d)) set.delete(d); else set.add(d);
    setCfg({ ...cfg, weekdays: Array.from(set).sort() });
  };

  const addHoliday = () => {
    const d = (newHoliday || "").trim();
    if (!d) return;
    if (cfg.holidays.includes(d)) { setNewHoliday(""); return; }
    setCfg({ ...cfg, holidays: [...cfg.holidays, d].sort() });
    setNewHoliday("");
  };

  const removeHoliday = (d) => {
    setCfg({ ...cfg, holidays: cfg.holidays.filter((x) => x !== d) });
  };

  const save = async () => {
    if (cfg.weekdays.length === 7) {
      toast.error(t("risk.nwd.cannotAllNonWorking"));
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/engagements/${engagementId}/non-working-days`, cfg);
      toast.success(t("risk.nwd.saved"));
      onSaved?.();
    } catch (e) {
      toast.error(toErrMsg(e, t("risk.toasts.saveFailed")));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="nwd-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <CalendarDays className="h-4 w-4 text-emerald-700" />
          {t("risk.nwd.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          {t("risk.nwd.desc").split(/\{weekendBold\}|\{refreshBold\}/).map((chunk, i, arr) => (
            <React.Fragment key={i}>
              {chunk}
              {i === 0 && arr.length > 1 && <b>{t("risk.nwd.weekendBold")}</b>}
              {i === 1 && arr.length > 2 && <b>{t("risk.nwd.refreshBold")}</b>}
            </React.Fragment>
          ))}
        </p>
        <div>
          <Label className="text-xs">{t("risk.nwd.weekendDays")}</Label>
          <div className="mt-1 flex flex-wrap gap-3">
            {WEEKDAY_KEYS.map((key, i) => (
              <label key={i} className="flex items-center gap-1.5 text-xs"
                     data-testid={`nwd-day-${i}`}>
                <Checkbox checked={cfg.weekdays.includes(i)}
                          disabled={!canEdit || loading}
                          onCheckedChange={() => toggleDay(i)}
                          data-testid={`nwd-day-cb-${i}`} />
                {t(`risk.nwd.days.${key}`)}
              </label>
            ))}
          </div>
        </div>
        <div>
          <Label className="text-xs">{t("risk.nwd.publicHolidays")}</Label>
          <div className="mt-1 flex flex-wrap gap-1.5" data-testid="nwd-holidays">
            {cfg.holidays.length === 0 && (
              <span className="text-[11px] text-muted-foreground">{t("risk.nwd.noHolidays")}</span>
            )}
            {cfg.holidays.map((d) => (
              <span key={d}
                    className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200"
                    data-testid={`nwd-holiday-${d}`}>
                {fmtDate(d)}{/* Batch 10 · B9 (F-82): DD-MMM-YYYY, never the stored ISO */}
                {canEdit && (
                  <button type="button" onClick={() => removeHoliday(d)}
                          className="opacity-70 hover:opacity-100"
                          data-testid={`nwd-holiday-remove-${d}`}>
                    <X className="h-3 w-3" />
                  </button>
                )}
              </span>
            ))}
          </div>
          {canEdit && (
            <div className="mt-2 flex items-center gap-2">
              <DateInput
                     value={newHoliday}
                     onChange={(v) => setNewHoliday(v)}
                     className="h-8 max-w-[180px] text-xs"
                     data-testid="nwd-holiday-input" />
              <Button type="button" size="sm" variant="outline"
                      onClick={addHoliday} disabled={!newHoliday}
                      data-testid="nwd-holiday-add">
                <Plus className="me-1 h-3.5 w-3.5" /> {t("risk.nwd.add")}
              </Button>
            </div>
          )}
        </div>
        {canEdit && (
          <div className="pt-1">
            <Button size="sm" onClick={save} disabled={saving || loading}
                    data-testid="nwd-save">
              {saving ? <Loader2 className="me-1 h-3.5 w-3.5 animate-spin" /> : null}
              {t("risk.nwd.save")}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// --------- L2 · Risk \u2192 Procedure multi-select (round 01-Oct-2026) ---------
// Loads the engagement procedure library, pre-filters to the suggested
// groups for the risk's fs_area, and lets the auditor pick refs. All
// refs are rendered LTR-isolated so Arabic risk descriptions wrapping
// them keep ascending ref order.
function ProcedureLinker({ engagementId, fsArea, value, onChange,
                              testidPrefix, t }) {
  const [procs, setProcs] = useState([]);
  const [groupsSuggested, setGroupsSuggested] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [showAll, setShowAll] = useState(false);
  useEffect(() => {
    let alive = true;
    if (!engagementId) return;
    setLoading(true); setErr("");
    api.get(`/engagements/${engagementId}/risk-procedure-candidates`,
             { params: { fs_area: fsArea || "" }, _silent: true })
       .then((r) => {
         if (!alive) return;
         setProcs(r.data?.procedures || []);
         setGroupsSuggested(r.data?.suggested_groups || []);
       })
       .catch(() => { if (alive) setErr(t("risk.linkedProceduresLoadError")); })
       .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [engagementId, fsArea, t]);
  const vSet = new Set(value || []);
  const toggle = (ref) => {
    const n = new Set(vSet);
    if (n.has(ref)) n.delete(ref); else n.add(ref);
    onChange(Array.from(n));
  };
  const visible = showAll ? procs : procs.filter((p) => p.suggested);
  return (
    <div data-testid={`${testidPrefix}-proc-linker`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Label className="text-xs">{t("risk.linkedProceduresLabel")}</Label>
        <div className="flex items-center gap-2 text-[11px]">
          <span className="text-muted-foreground"
                data-testid={`${testidPrefix}-proc-linker-count`}>
            {(value || []).length === 0
              ? t("risk.linkedProceduresNone")
              : t("risk.linkedProceduresCount").replace("{n}", String((value || []).length))}
          </span>
          <Button type="button" size="sm" variant="outline"
                  className="h-7 text-[11px]"
                  onClick={() => setShowAll((v) => !v)}
                  data-testid={`${testidPrefix}-proc-linker-toggle-all`}>
            {showAll
              ? t("risk.linkedProceduresSuggested").replace("{area}", fsArea || "—")
              : t("risk.linkedProceduresAll")}
          </Button>
        </div>
      </div>
      <p className="mt-0.5 text-[11px] text-muted-foreground">
        {t("risk.linkedProceduresHelp")}
      </p>
      {loading && (
        <div className="mt-2 flex items-center gap-2 text-[11px] text-muted-foreground"
             data-testid={`${testidPrefix}-proc-linker-loading`}>
          <Loader2 className="h-3 w-3 animate-spin" /> {t("common.loading") || "…"}
        </div>
      )}
      {err && (
        <div className="mt-2 rounded border border-red-300 bg-red-50 p-1.5 text-[11px] text-red-800"
             data-testid={`${testidPrefix}-proc-linker-err`}>{err}</div>
      )}
      {!loading && !err && visible.length === 0 && (
        <div className="mt-2 text-[11px] text-muted-foreground"
             data-testid={`${testidPrefix}-proc-linker-empty`}>
          {t("risk.linkedProceduresEmpty")}
        </div>
      )}
      {!loading && !err && visible.length > 0 && (
        <div className="mt-2 max-h-56 space-y-1 overflow-y-auto rounded border bg-slate-50 p-2 dark:bg-slate-900/40"
             data-testid={`${testidPrefix}-proc-linker-list`}>
          {visible.map((p) => (
            <label key={p.ref}
                   className="flex items-start gap-2 rounded px-1 py-0.5 text-[11px] hover:bg-white dark:hover:bg-slate-800"
                   data-testid={`${testidPrefix}-proc-opt-${p.ref}`}>
              <Checkbox checked={vSet.has(p.ref)}
                        onCheckedChange={() => toggle(p.ref)}
                        data-testid={`${testidPrefix}-proc-cb-${p.ref}`} />
              <span className="flex-1">
                <span className="font-mono text-[10px] text-emerald-800 dark:text-emerald-300"
                      dir="ltr">{`\u2068${p.ref}\u2069`}</span>
                <span className="ms-1 text-muted-foreground">· {p.group_name_en}</span>
                <span className="block text-[11px]">{p.procedure_en}</span>
              </span>
            </label>
          ))}
        </div>
      )}
      {groupsSuggested.length > 0 && !showAll && (
        <p className="mt-1 text-[10px] text-muted-foreground"
           data-testid={`${testidPrefix}-proc-linker-suggested-note`}>
          {t("risk.linkedProceduresSuggested").replace("{area}", fsArea || "—")}
        </p>
      )}
    </div>
  );
}
